// src/services/eventsService.js
import axios from "@/axios";

/**
 * Servicio para manejar eventos desde la API
 */
class EventsService {
  /**
   * Obtiene todos los eventos disponibles
   * @param {Object} params - Parámetros para filtrar eventos (opcionales)
   * @returns {Promise} - Promesa con los eventos
   */
  async getEvents(params = {}) {
    try {
      const response = await axios.get("/events", { params });
      return response.data;
    } catch (error) {
      console.error("Error al obtener eventos:", error);
      throw error;
    }
  }

  /**
   * Obtiene un evento específico por su ID
   * @param {Number} id - ID del evento
   * @returns {Promise} - Promesa con los detalles del evento
   */
  async getEventById(id) {
    try {
      const response = await axios.get(`/events/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error al obtener el evento con ID ${id}:`, error);
      throw error;
    }
  }

  /**
   * Obtiene eventos filtrados por país
   * @param {Number} countryId - ID del país
   * @returns {Promise} - Promesa con los eventos del país
   */
  async getEventsByCountry(countryId) {
    try {
      const response = await axios.get("/events", {
        params: { country_id: countryId },
      });
      return response.data;
    } catch (error) {
      console.error(`Error al obtener eventos del país ${countryId}:`, error);
      throw error;
    }
  }

  /**
   * Obtiene eventos próximos (a partir de la fecha actual)
   * @param {Number} limit - Límite de eventos a obtener
   * @returns {Promise} - Promesa con los próximos eventos
   */
  async getUpcomingEvents(limit = 10) {
    try {
      const response = await axios.get("/events", {
        params: {
          upcoming: true,
          limit: limit,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error al obtener próximos eventos:", error);
      throw error;
    }
  }
  /**
   * Obtiene los eventos en los que el usuario está registrado
   * @returns {Promise} - Promesa con los eventos del usuario
   */
  async getMyEvents() {
    try {
      const response = await axios.get("/v1/my-events");
      return response.data;
    } catch (error) {
      console.error("Error al obtener mis eventos:", error);
      throw error;
    }
  }

  /**
   * Registra un usuario en un evento
   * @param {Number} eventId - ID del evento
   * @param {Object} userData - Datos del usuario para registro
   * @returns {Promise} - Promesa con respuesta del registro
   */
  async registerForEvent(eventId, userData) {
    try {
      const response = await axios.post(
        `/events/${eventId}/register`,
        userData
      );
      return response.data;
    } catch (error) {
      console.error(`Error al registrarse en el evento ${eventId}:`, error);
      throw error;
    }
  }

  /**
   * Registra un usuario en la lista de espera de un evento
   * @param {Number} eventId - ID del evento
   * @param {Object} userData - Datos del usuario para la lista de espera
   * @returns {Promise} - Promesa con respuesta del registro en lista de espera
   */
  async joinWaitlist(eventId, userData) {
    try {
      const response = await axios.post(
        `/events/${eventId}/waitlist`,
        userData
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error al unirse a la lista de espera del evento ${eventId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Cancela registro de un usuario en un evento
   * @param {Number} eventId - ID del evento
   * @param {Number} registrationId - ID del registro
   * @returns {Promise} - Promesa con respuesta de la cancelación
   */
  async cancelRegistration(eventId, registrationId) {
    try {
      const response = await axios.delete(
        `/events/${eventId}/registrations/${registrationId}`
      );
      return response.data;
    } catch (error) {
      console.error(
        `Error al cancelar registro ${registrationId} del evento ${eventId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Formatea la fecha de un evento para mostrarla en la UI
   * @param {String} dateString - Fecha del evento en formato ISO
   * @returns {Object} - Objeto con distintos formatos de fecha
   */
  formatEventDate(dateString) {
    const date = new Date(dateString);

    // Formato corto: DD/MM/YYYY
    const shortFormat = date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    // Formato mediano: DD MMM, YYYY
    const mediumFormat = date.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

    // Formato largo: Día, DD de Mes de YYYY
    const longFormat = date.toLocaleDateString("es-ES", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });

    // Componentes individuales para mayor flexibilidad
    const components = {
      day: date.getDate(),
      month: date.getMonth() + 1,
      monthName: date.toLocaleDateString("es-ES", { month: "long" }),
      monthShort: date.toLocaleDateString("es-ES", { month: "short" }),
      year: date.getFullYear(),
      weekday: date.toLocaleDateString("es-ES", { weekday: "long" }),
      weekdayShort: date.toLocaleDateString("es-ES", { weekday: "short" }),
      time: date.toLocaleTimeString("es-ES", {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };

    return {
      short: shortFormat,
      medium: mediumFormat,
      long: longFormat,
      components,
      original: dateString,
      date, // Objeto Date para manipulaciones adicionales
    };
  }
  formatEventTime(timeString) {
    if (!timeString) {
      return "";
    }

    try {
      const timeParts = timeString.split(" ")[1].split(":");
      let hours = parseInt(timeParts[0]);
      const minutes = timeParts[1];

      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12;

      return `${hours}:${minutes} ${ampm}`;
    } catch (error) {
      console.error("Error formatting time:", error, "for input:", timeString);
      return "";
    }
  }
  /**
   * Comprueba si un evento está completamente lleno
   * @param {Object} event - Objeto evento
   * @returns {Boolean} - true si está lleno, false en caso contrario
   */
  isEventFullyBooked(event) {
    return event.current_attendees >= event.max_attendees;
  }

  /**
   * Comprueba si un evento tiene lista de espera disponible
   * @param {Object} event - Objeto evento
   * @returns {Boolean} - true si hay lista de espera disponible, false en caso contrario
   */
  hasWaitlistAvailable(event) {
    return (
      event.waitlist_size > 0 && event.current_waitlist < event.waitlist_size
    );
  }

  /**
   * Calcula el porcentaje de ocupación de un evento
   * @param {Object} event - Objeto evento
   * @returns {Number} - Porcentaje de ocupación (0-100)
   */
  getOccupancyPercentage(event) {
    if (event.max_attendees === 0) return 0;
    return Math.min(
      100,
      Math.round((event.current_attendees / event.max_attendees) * 100)
    );
  }

  /**
   * Filtra eventos por nivel de dificultad
   * @param {Array} events - Array de eventos
   * @param {String} level - Nivel de dificultad ('Fácil', 'Moderado', 'Difícil')
   * @returns {Array} - Eventos filtrados
   */
  filterEventsByLevel(events, level) {
    if (!level) return events;
    return events.filter((event) => event.level === level);
  }

  /**
   * Filtra eventos por país
   * @param {Array} events - Array de eventos
   * @param {Number} countryId - ID del país
   * @returns {Array} - Eventos filtrados
   */
  filterEventsByCountry(events, countryId) {
    if (!countryId) return events;
    return events.filter((event) => event.country.id === countryId);
  }

  /**
   * Ordena eventos por fecha (ascendente o descendente)
   * @param {Array} events - Array de eventos
   * @param {Boolean} ascending - true para orden ascendente, false para descendente
   * @returns {Array} - Eventos ordenados
   */
  sortEventsByDate(events, ascending = true) {
    return [...events].sort((a, b) => {
      const dateA = new Date(a.event_date);
      const dateB = new Date(b.event_date);
      return ascending ? dateA - dateB : dateB - dateA;
    });
  }

  isInPreregistration(event) {
    return event.is_in_preregistration || false;
  }

  isPreregistrationEnded(event) {
    return event.is_preregistration_ended || false;
  }

  filterPublicEvents(events) {
    return events.filter(event => !this.isInPreregistration(event));
  }

  filterPreregistrationEvents(events) {
    return events.filter(event => this.isInPreregistration(event));
  }
}

// Exportar una instancia única del servicio
export default new EventsService();
